import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    Popover,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import ChatBubbleOutlineIcon from '@material-ui/icons/ChatBubbleOutline'
import CloseIcon from '@material-ui/icons/Close'

import { TenantMessage, ThreadReply } from '../../../models/TenantMessage'
import { useSendLeadMessage } from '../../../hooks/Outreach/useLeadCrm'
import {
    useMarkRepliesRead,
    useReplyToMessage,
    useTenantMessageReplies,
    useTenantMessages,
} from '../../../hooks/useTenantMessages'

const useStyles = makeStyles((theme: Theme) => ({
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    sectionTitle: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    sectionTitleIcon: {
        fontSize: 16,
        color: theme.palette.primary.main,
        flexShrink: 0,
    },
    newMessageButton: {
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.75rem',
        lineHeight: 1.2,
        minWidth: 0,
        minHeight: 0,
        padding: '4px 10px 4px 6px',
        borderRadius: 16,
        '& .MuiButton-startIcon': {
            marginRight: 4,
            marginLeft: 0,
        },
        '& .MuiButton-startIcon > *:first-child': {
            fontSize: 16,
        },
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
    },
    th: {
        fontSize: '0.65rem',
        fontWeight: 700,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
        textAlign: 'left',
        padding: theme.spacing(0.5, 1, 0.75, 0),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    thCount: {
        width: 128,
        paddingRight: theme.spacing(1.5),
    },
    thSent: {
        textAlign: 'right',
        paddingRight: 0,
        width: 92,
    },
    row: {
        cursor: 'pointer',
        '&:hover td': {
            backgroundColor: theme.palette.action.hover,
        },
    },
    td: {
        fontSize: '0.8125rem',
        padding: theme.spacing(0.85, 1, 0.85, 0),
        borderBottom: `1px solid ${theme.palette.divider}`,
        verticalAlign: 'middle',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    countCell: {
        width: 128,
        paddingRight: theme.spacing(1.5),
        overflow: 'visible',
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        fontVariantNumeric: 'tabular-nums',
    },
    contentCell: {
        color: theme.palette.text.primary,
    },
    subject: {
        fontWeight: 700,
    },
    sentCell: {
        width: 92,
        color: theme.palette.text.secondary,
        fontSize: '0.7rem',
        textAlign: 'right',
        paddingRight: 0,
    },
    snippet: {
        color: theme.palette.text.secondary,
        fontWeight: 400,
    },
    countMark: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
    },
    countNew: {
        color: '#008C85',
        fontWeight: 600,
    },
    statusDot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: '#9ca3af',
        flexShrink: 0,
    },
    statusDotUnread: {
        backgroundColor: '#008C85',
    },
    countIcon: {
        fontSize: 13,
        color: '#9ca3af',
    },
    countIconUnread: {
        color: '#008C85',
    },
    empty: {
        color: theme.palette.text.disabled,
        fontSize: '0.8125rem',
    },
    composePaper: {
        width: 360,
        maxWidth: 'calc(100vw - 32px)',
        padding: theme.spacing(1.5),
        borderRadius: 10,
    },
    threadPaper: {
        width: 480,
        maxWidth: '100%',
        height: 'min(640px, 80vh)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    threadHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        padding: theme.spacing(1.5, 1.5, 1.25),
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    threadSubject: {
        fontWeight: 700,
        fontSize: '0.95rem',
        lineHeight: 1.3,
    },
    original: {
        padding: theme.spacing(1.25, 1.5),
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
    originalLabel: {
        fontSize: '0.5625rem',
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    originalBody: {
        fontSize: '0.8125rem',
        whiteSpace: 'pre-wrap',
        marginTop: 4,
        wordBreak: 'break-word',
    },
    replies: {
        flex: 1,
        overflowY: 'auto',
        padding: theme.spacing(1.5, 1.5, 1),
    },
    replyBar: {
        padding: theme.spacing(1.25, 1.5, 1.5),
        borderTop: `1px solid ${theme.palette.divider}`,
        flexShrink: 0,
    },
}))

function formatDateTime(value: string | null | undefined): string {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

function previewBody(body: string | null | undefined): string {
    return (body || '').replace(/\s+/g, ' ').trim()
}

function threadFrom(message: TenantMessage, tenantName: string): string {
    if (message.initiated_by_tenant) {
        return tenantName || message.created_by_name || 'Tenant'
    }
    return message.created_by_name || 'Staff'
}

function threadTotals(
    message: TenantMessage,
): { total: number; newCount: number } {
    const total = 1 + (message.reply_count || 0)
    const unreadReplies = message.unread_reply_count ?? 0
    const newCount = message.has_unread_replies ? Math.max(unreadReplies, 1) : 0
    return { total, newCount }
}

function threadCountTitle(total: number, newCount: number): string {
    if (newCount > 0) {
        return `${total} in thread, ${newCount} new`
    }
    return `${total} in thread`
}

interface Props {
    leadId: number
    tenantId: number
    tenantName: string
    readOnly?: boolean
}

export const LeadMessagesSection: React.FC<Props> = ({
    leadId,
    tenantId,
    tenantName,
    readOnly = false,
}) => {
    const classes = useStyles()
    const [composeAnchor, setComposeAnchor] = useState<HTMLElement | null>(null)
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [selected, setSelected] = useState<TenantMessage | null>(null)

    const { sendMessage, isSending } = useSendLeadMessage(leadId)
    const messageFilters = useMemo(
        () => ({
            tenant_id: tenantId,
            individual: true as const,
            page_size: 25,
        }),
        [tenantId],
    )
    const { messages, isLoading } = useTenantMessages(messageFilters)

    const closeCompose = () => {
        setComposeAnchor(null)
        setSubject('')
        setBody('')
    }

    const handleSend = async () => {
        if (!subject.trim() || !body.trim()) return
        await sendMessage({
            subject: subject.trim(),
            body: body.trim(),
        })
        closeCompose()
    }

    return (
        <Box>
            <Box className={classes.header}>
                <Typography className={classes.sectionTitle}>
                    <ChatBubbleOutlineIcon
                        className={classes.sectionTitleIcon}
                    />
                    Messages
                </Typography>
                {!readOnly && (
                    <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        className={classes.newMessageButton}
                        onClick={(event) =>
                            setComposeAnchor(event.currentTarget)
                        }
                    >
                        Compose
                    </Button>
                )}
            </Box>

            {isLoading ? (
                <CircularProgress size={18} />
            ) : messages.length === 0 ? (
                <Typography className={classes.empty}>No messages.</Typography>
            ) : (
                <table className={classes.table}>
                    <thead>
                        <tr>
                            <th className={`${classes.th} ${classes.thCount}`}>
                                Count
                            </th>
                            <th className={classes.th}>Subject</th>
                            <th className={`${classes.th} ${classes.thSent}`}>
                                Sent
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {messages.map((message) => {
                            const snippet = previewBody(message.body)
                            const { total, newCount } = threadTotals(message)
                            return (
                                <tr
                                    key={message.id}
                                    className={classes.row}
                                    onClick={() => setSelected(message)}
                                >
                                    <td
                                        className={`${classes.td} ${classes.countCell}`}
                                        title={threadCountTitle(
                                            total,
                                            newCount,
                                        )}
                                    >
                                        <span className={classes.countMark}>
                                            <span
                                                className={`${
                                                    classes.statusDot
                                                } ${
                                                    newCount > 0
                                                        ? classes.statusDotUnread
                                                        : ''
                                                }`}
                                            />
                                            <ChatBubbleOutlineIcon
                                                className={`${
                                                    classes.countIcon
                                                } ${
                                                    newCount > 0
                                                        ? classes.countIconUnread
                                                        : ''
                                                }`}
                                            />
                                            <span>
                                                {total}
                                                {newCount > 0 && (
                                                    <span
                                                        className={
                                                            classes.countNew
                                                        }
                                                    >
                                                        {` (${newCount} new)`}
                                                    </span>
                                                )}
                                            </span>
                                        </span>
                                    </td>
                                    <td
                                        className={`${classes.td} ${classes.contentCell}`}
                                        title={
                                            snippet
                                                ? `${message.subject} — ${snippet}`
                                                : message.subject
                                        }
                                    >
                                        <span className={classes.subject}>
                                            {message.subject}
                                        </span>
                                        {snippet ? (
                                            <span className={classes.snippet}>
                                                {' — '}
                                                {snippet}
                                            </span>
                                        ) : null}
                                    </td>
                                    <td
                                        className={`${classes.td} ${classes.sentCell}`}
                                    >
                                        {formatDateTime(message.created_date)}
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            )}

            <Popover
                open={Boolean(composeAnchor)}
                anchorEl={composeAnchor}
                onClose={closeCompose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ className: classes.composePaper }}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Subject"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    inputProps={{ maxLength: 255 }}
                    style={{ marginBottom: 8 }}
                />
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    placeholder="Write a message. They’ll get an email to open the portal."
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                />
                <Box display="flex" justifyContent="flex-end" mt={1}>
                    <Button
                        size="small"
                        color="primary"
                        variant="contained"
                        disabled={isSending || !subject.trim() || !body.trim()}
                        onClick={handleSend}
                    >
                        Send message
                    </Button>
                </Box>
            </Popover>

            <LeadThreadDialog
                message={selected}
                tenantId={tenantId}
                tenantName={tenantName}
                readOnly={readOnly}
                onClose={() => setSelected(null)}
            />
        </Box>
    )
}

interface ThreadDialogProps {
    message: TenantMessage | null
    tenantId: number
    tenantName: string
    readOnly: boolean
    onClose: () => void
}

const LeadThreadDialog: React.FC<ThreadDialogProps> = ({
    message,
    tenantId,
    tenantName,
    readOnly,
    onClose,
}) => {
    const classes = useStyles()
    const { replies, isLoading } = useTenantMessageReplies(
        message?.id ?? null,
        tenantId,
    )
    const replyMutation = useReplyToMessage()
    const markReadMutation = useMarkRepliesRead()
    const [replyBody, setReplyBody] = useState('')
    const repliesEndRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (message?.id) {
            markReadMutation.mutate(message.id)
            setReplyBody('')
        }
    }, [message?.id])

    useEffect(() => {
        repliesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [replies.length])

    const handleReply = () => {
        if (!message || !replyBody.trim()) return
        replyMutation.mutate(
            {
                messageId: message.id,
                body: replyBody.trim(),
                recipient: tenantId,
            },
            { onSuccess: () => setReplyBody('') },
        )
    }

    return (
        <Dialog
            open={Boolean(message)}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            disableEnforceFocus
            classes={{ paper: classes.threadPaper }}
        >
            {message && (
                <>
                    <Box className={classes.threadHeader}>
                        <Box flex={1} minWidth={0}>
                            <Typography className={classes.threadSubject}>
                                {message.subject}
                            </Typography>
                            <Typography className={classes.empty}>
                                {threadFrom(message, tenantName)}
                                {message.created_date
                                    ? ` · ${formatDateTime(
                                          message.created_date,
                                      )}`
                                    : ''}
                            </Typography>
                        </Box>
                        <IconButton size="small" onClick={onClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Box className={classes.original}>
                        <Typography className={classes.originalLabel}>
                            Original message
                        </Typography>
                        <Typography className={classes.originalBody}>
                            {message.body}
                        </Typography>
                    </Box>

                    <Box className={classes.replies}>
                        {isLoading ? (
                            <Box
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight={120}
                            >
                                <CircularProgress size={22} />
                            </Box>
                        ) : replies.length === 0 ? (
                            <Typography className={classes.empty}>
                                {readOnly
                                    ? 'No replies in this thread.'
                                    : 'No replies yet. Send the first reply below.'}
                            </Typography>
                        ) : (
                            <>
                                {replies.map((reply) => (
                                    <ReplyBubble key={reply.id} reply={reply} />
                                ))}
                                <div ref={repliesEndRef} />
                            </>
                        )}
                    </Box>

                    {!readOnly && (
                        <Box className={classes.replyBar}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                variant="outlined"
                                placeholder="Reply…"
                                value={replyBody}
                                onChange={(event) =>
                                    setReplyBody(event.target.value)
                                }
                            />
                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                mt={1}
                            >
                                <Button
                                    size="small"
                                    color="primary"
                                    variant="contained"
                                    disabled={
                                        !replyBody.trim() ||
                                        replyMutation.isLoading
                                    }
                                    onClick={handleReply}
                                >
                                    Send reply
                                </Button>
                            </Box>
                        </Box>
                    )}
                </>
            )}
        </Dialog>
    )
}

const ReplyBubble: React.FC<{ reply: ThreadReply }> = ({ reply }) => {
    const isStaff = reply.sender_type === 'STAFF'
    return (
        <Box
            display="flex"
            flexDirection={isStaff ? 'row-reverse' : 'row'}
            mb={1.5}
            style={{ gap: 8 }}
        >
            <Box style={{ maxWidth: '80%', minWidth: 80 }}>
                <Box
                    display="flex"
                    justifyContent={isStaff ? 'flex-end' : 'flex-start'}
                    style={{ gap: 6, marginBottom: 2 }}
                >
                    <Typography
                        variant="caption"
                        style={{
                            fontWeight: 600,
                            fontSize: '0.6875rem',
                            color: isStaff ? '#008C85' : '#374151',
                        }}
                    >
                        {reply.sender_name}
                    </Typography>
                    <Typography
                        variant="caption"
                        style={{ fontSize: '0.5625rem', color: '#9ca3af' }}
                    >
                        {formatDateTime(reply.created_date)}
                    </Typography>
                </Box>
                <Box
                    style={{
                        padding: '8px 12px',
                        borderRadius: isStaff
                            ? '12px 4px 12px 12px'
                            : '4px 12px 12px 12px',
                        backgroundColor: isStaff ? '#e8f5f3' : '#f4f5f7',
                    }}
                >
                    <Typography
                        variant="body2"
                        style={{
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.8125rem',
                            wordBreak: 'break-word',
                        }}
                    >
                        {reply.body}
                    </Typography>
                </Box>
            </Box>
        </Box>
    )
}
